import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'atlas-ide:v2:recent-files';
const MAX_RECENT_FILES = 15;

export type RecentFile = {
  path: string;
  name: string;
  timestamp: number;
};

export function useRecentFiles() {
  const [recentFiles, setRecentFiles] = useState<RecentFile[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved) as RecentFile[];
      }
    } catch {
      // Ignore parse errors
    }
    return [];
  });

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(recentFiles));
  }, [recentFiles]);

  // Add a file to recent files list
  const addRecentFile = useCallback((path: string) => {
    const name = path.split('/').pop() || path;
    setRecentFiles((prev) => {
      // Remove if already exists
      const filtered = prev.filter((f) => f.path !== path);
      // Add to front with current timestamp
      const newEntry: RecentFile = { path, name, timestamp: Date.now() };
      const updated = [newEntry, ...filtered];
      // Limit to max items
      return updated.slice(0, MAX_RECENT_FILES);
    });
  }, []);

  // Remove a file from recent files list
  const removeRecentFile = useCallback((path: string) => {
    setRecentFiles((prev) => prev.filter((f) => f.path !== path));
  }, []);

  // Clear all recent files
  const clearRecentFiles = useCallback(() => {
    setRecentFiles([]);
  }, []);

  return {
    recentFiles,
    addRecentFile,
    removeRecentFile,
    clearRecentFiles,
  };
}
